<script setup lang="ts">
// Tlačítko: jedno primární na obrazovku, min. výška 48 px, glow jen primární v tmavém režimu.
// `as` umožní vykreslit odkaz (například RouterLink) se stejným vzhledem; cíl se předá jako atribut `to`.
import type { Component } from 'vue'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'md' | 'lg'

withDefaults(
  defineProps<{
    variant?: ButtonVariant
    size?: ButtonSize
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    block?: boolean
    as?: string | Component
  }>(),
  {
    variant: 'secondary',
    size: 'md',
    type: 'button',
    disabled: false,
    loading: false,
    block: false,
    as: 'button',
  },
)

const emit = defineEmits<{ click: [event: MouseEvent] }>()
</script>

<template>
  <component
    :is="as"
    :type="as === 'button' ? type : undefined"
    class="wb-button"
    :class="[`wb-button--${variant}`, `wb-button--${size}`, { 'wb-button--block': block }]"
    :disabled="as === 'button' ? disabled || loading : undefined"
    :aria-busy="loading || undefined"
    @click="emit('click', $event)"
  >
    <span v-if="$slots.icon" class="wb-button__icon" aria-hidden="true"><slot name="icon" /></span>
    <span class="wb-button__label"><slot /></span>
  </component>
</template>

<style scoped>
.wb-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: var(--tap-min);
  padding: 0 var(--space-5);
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  font-family: var(--font-body);
  font-weight: 600;
  font-size: var(--text-base);
  line-height: 1.2;
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color var(--dur-fast) var(--ease),
    border-color var(--dur-fast) var(--ease),
    color var(--dur-fast) var(--ease),
    box-shadow var(--dur-base) var(--ease);
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.wb-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.wb-button--lg {
  min-height: 56px;
  padding: 0 var(--space-6);
  font-size: var(--text-lg);
}
.wb-button--block {
  display: flex;
  width: 100%;
}
.wb-button__icon {
  display: inline-flex;
}

.wb-button--primary {
  background: var(--primary);
  color: var(--on-primary);
  box-shadow: var(--glow);
}
.wb-button--primary:hover:not(:disabled) {
  background: var(--primary-hover);
}

.wb-button--secondary {
  background: var(--surface);
  color: var(--text);
  border-color: var(--border);
}
.wb-button--secondary:hover:not(:disabled) {
  background: var(--surface-2);
  border-color: var(--border-strong);
}

.wb-button--ghost {
  background: transparent;
  color: var(--primary-text);
}
.wb-button--ghost:hover:not(:disabled) {
  background: var(--primary-soft);
}

.wb-button--danger {
  background: var(--danger-bg);
  color: var(--danger);
  border-color: var(--danger-border);
}
</style>
